import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, DatePicker, Modal, Space, Spin, Tabs, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import SimulatorBulkGenerator from "./SimulatorBulkGenerator";
import SimulatorCleanupPanel from "./SimulatorCleanupPanel";
import SimulatorHabitsTab from "./tabs/SimulatorHabitsTab";
import SimulatorMeasurementsTab from "./tabs/SimulatorMeasurementsTab";
import SimulatorNutritionTab from "./tabs/SimulatorNutritionTab";
import SimulatorWorkoutTab from "./tabs/SimulatorWorkoutTab";
import { useSimulatorTarget } from "./useSimulatorTarget";

type Props = {
  traineeId: string;
  open: boolean;
  onClose: () => void;
};

/** Zakładki, które działają na wybranym dniu — tylko one potrzebują pickera daty w nagłówku. */
const DAY_SCOPED_TABS = ["workout", "nutrition", "habits", "measurements"];

const TraineeSimulatorModal = ({ traineeId, open, onClose }: Props) => {
  const queryClient = useQueryClient();
  const { userId } = useUserRole();
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [activeTab, setActiveTab] = useState("workout");
  const target = useSimulatorTarget(traineeId, open);

  const onWritten = () => {
    void queryClient.invalidateQueries({ queryKey: ["simulator", "log", traineeId] });
  };

  const selectedDate = date.format("YYYY-MM-DD");

  const tabProps = target.data ? { target: target.data, date: selectedDate, createdBy: userId, onWritten } : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      destroyOnClose
      title={
        <Space direction="vertical" size={0}>
          <span>Symulator działań klientki</span>
          {target.data ? (
            <Typography.Text type="secondary" className="text-sm font-normal">
              {[[target.data.firstName, target.data.lastName].filter(Boolean).join(" "), target.data.email]
                .filter(Boolean)
                .join(" · ")}
            </Typography.Text>
          ) : null}
        </Space>
      }
    >
      {!userId ? (
        // `useUserRole` zwraca pusty string, dopóki użytkownik się nie załaduje, a `createdBy`
        // ląduje w kolumnie `uuid not null` — bez tego pierwszy zapis padłby na typie w sposób,
        // którego nie da się powiązać z przyczyną.
        <Spin />
      ) : target.isLoading ? (
        <Spin />
      ) : target.isError ? (
        <Alert type="error" showIcon message={(target.error as Error).message} />
      ) : tabProps ? (
        <Space direction="vertical" size={16} className="w-full">
          <Alert
            type="warning"
            showIcon
            message="Zapisy są nieodróżnialne od działań klientki"
            description="Wchodzą do rankingów, liczników i odznak, a powiadomienia polecą normalnie. Zadania typu planActivity punktują z client_activity, które powstaje dopiero po przejściu webhooka — punkty pojawią się z opóźnieniem."
          />

          {/* Wybór dnia dotyczy tylko zakładek działających „na dzień". Generator ma własny zakres
              dat, a rejestr obejmuje całą historię — pokazywanie im tego pickera wprowadzałoby w błąd. */}
          {DAY_SCOPED_TABS.includes(activeTab) ? (
            <Space wrap align="center">
              <Typography.Text>Działam w imieniu klientki na dzień:</Typography.Text>
              {/* Bez ograniczenia daty — wyzwania sięgają w przyszłość, a bez przyszłych dni nie da się
                  sprawdzić, jak ranking zachowa się do końca ich okna. */}
              <DatePicker value={date} allowClear={false} onChange={(value) => value && setDate(value)} />
              {date.isSame(dayjs(), "day") ? (
                <Tag color="blue">dzisiaj</Tag>
              ) : date.isAfter(dayjs(), "day") ? (
                <Tag color="red">w przód</Tag>
              ) : (
                <Tag color="orange">wstecz</Tag>
              )}
            </Space>
          ) : null}

          <Tabs
            destroyInactiveTabPane
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: "workout", label: "Trening", children: <SimulatorWorkoutTab {...tabProps} /> },
              { key: "nutrition", label: "Dieta", children: <SimulatorNutritionTab {...tabProps} /> },
              { key: "habits", label: "Nawyki", children: <SimulatorHabitsTab {...tabProps} /> },
              { key: "measurements", label: "Pomiary", children: <SimulatorMeasurementsTab {...tabProps} /> },
              {
                key: "generator",
                label: "Generator",
                children: <SimulatorBulkGenerator target={tabProps.target} createdBy={userId} onWritten={onWritten} />,
              },
              {
                key: "history",
                label: "Dane symulatora",
                children: <SimulatorCleanupPanel traineeId={traineeId} />,
              },
            ]}
          />
        </Space>
      ) : null}
    </Modal>
  );
};

export default TraineeSimulatorModal;
